import { useEffect, useState } from 'react';
import { User } from 'lucide-react';

/**
 * Opens the account panel, and shows who is signed in.
 *
 * Three states, in order of preference: the user's photo, their initials, or a
 * neutral person icon. If the photo fails to load, the component falls back
 * through the same order rather than showing a broken image. The fallback is React
 * state, not a DOM mutation inside an `onError` handler, which is how the old
 * build did it.
 *
 * Audit references prevented here: account.jsx #13, App.jsx #17, auth.jsx #14.
 *
 * @param {object} props
 * @param {() => void} props.onOpen
 * @param {string|null} [props.displayName]  Signed-in user's name, if known.
 * @param {string|null} [props.photoUrl]     Their avatar, if any.
 */
export function AccountButton({ onOpen, displayName = null, photoUrl = null }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const initials = initialsFrom(displayName);
  const showPhoto = Boolean(photoUrl) && !photoFailed;
  useEffect(() => setPhotoFailed(false), [photoUrl]);

  return (
    <button
      type="button"
      className="icon-btn account-btn"
      onClick={onOpen}
      aria-label={displayName ? `Account: ${displayName}` : 'Account'}
    >
      {showPhoto ? (
        <img
          className="account-btn__photo"
          src={photoUrl}
          alt=""
          onError={() => setPhotoFailed(true)}
        />
      ) : initials ? (
        <span aria-hidden="true">{initials}</span>
      ) : (
        <User size={17} aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * Up to two initials from a display name.
 *
 * @param {string|null} displayName
 * @returns {string} Empty string when there is no usable name, so the caller can
 *   fall back rather than render a stray character.
 */
function initialsFrom(displayName) {
  if (typeof displayName !== 'string') return '';
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
